import { useState, useEffect } from "react";
import {
    RefreshCw, CheckCircle2, XCircle, Clock,
    Share2, Server, Globe, Key, AlertCircle
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path

interface IloProtocols {
    id: number;
    asset_id: number;
    protocols: string; // JSON string
    snmp_settings: string; // JSON string
    sntp_current_time: string | null;
    sntp_timezone: string | null;
}

interface AssetIloProtocolsProps {
    assetId: string | number;
}

export default function AssetIloProtocols({ assetId }: AssetIloProtocolsProps) {
    const [data, setData] = useState<IloProtocols | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchProtocols = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get("/asset_ilo_protocols", { params: { asset_id: assetId } });

            if (response.data.status && response.data.data && response.data.data.length > 0) {
                setData(response.data.data[0]);
            } else {
                setData(null);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch protocols");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchProtocols();
        }
    }, [assetId]);

    // Helper to safely parse JSON strings
    const safeParseJSON = (jsonStr: string | null, fallback: any = {}) => {
        if (!jsonStr) return fallback;
        try {
            return JSON.parse(jsonStr);
        } catch (e) {
            return fallback;
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4 bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 h-full shadow-sm">
                <div className="w-8 h-8 border-2 border-teal-500/20 border-t-teal-500 rounded-full animate-spin"></div>
                <p className="text-xs font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Loading Protocols...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 opacity-80 h-full shadow-sm">
                <Share2 size={32} className="text-slate-400 dark:text-gray-500 mb-3" />
                <p className="text-xs font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest">No protocol data found</p>
            </div>
        );
    }

    const protocolsObj = safeParseJSON(data.protocols);
    const snmpObj = safeParseJSON(data.snmp_settings);

    return (
        <div className="flex flex-col text-slate-900 dark:text-gray-200 relative overflow-hidden pb-4 px-2">
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-4 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Share2 className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Protocols & Services</h2>
                </div>
                
                <button
                    onClick={fetchProtocols}
                    disabled={loading}
                    className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm cursor-pointer"
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : 'group-hover:text-teal-500'} />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column: SNTP and SNMP Info */}
                <div className="col-span-1 flex flex-col gap-4">
                    
                    {/* Time Settings Card */}
                    <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                        <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                            <Clock size={14} className="text-orange-500" /> SNTP Time Settings
                        </h3>
                        <div className="space-y-3 text-[11px]">
                            <div className="flex justify-between">
                                <span className="text-slate-500 dark:text-gray-400">Current Time:</span>
                                <span className="font-mono text-slate-800 dark:text-gray-300">
                                    {data.sntp_current_time ? new Date(data.sntp_current_time).toLocaleString() : "-"}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500 dark:text-gray-400">Timezone:</span>
                                <span className="text-slate-800 dark:text-gray-300 font-mono">
                                    {data.sntp_timezone || "-"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* SNMP Settings Card */}
                    <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                        <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                            <Server size={14} className="text-orange-500" /> SNMP Configuration
                        </h3>
                        <div className="space-y-3 text-[11px]">
                            <div className="flex justify-between">
                                <span className="text-slate-500 dark:text-gray-400">Read Community:</span>
                                <span className="font-mono text-slate-800 dark:text-gray-300">
                                    {snmpObj.read_community || "-"}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500 dark:text-gray-400">Trap Community:</span>
                                <span className="font-mono text-slate-800 dark:text-gray-300">
                                    {snmpObj.trap_community || "-"}
                                </span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-gray-700/30">
                                <span className="text-slate-500 dark:text-gray-400">SNMPv3 Enabled:</span>
                                <span>
                                    {snmpObj.snmpv3_enabled === true ? (
                                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">Yes</span>
                                    ) : snmpObj.snmpv3_enabled === false ? (
                                        <span className="text-slate-500 dark:text-gray-400 font-bold">No</span>
                                    ) : (
                                        <span className="text-slate-400 dark:text-gray-500 font-mono">-</span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Protocols Grid */}
                <div className="col-span-1 lg:col-span-2 bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <Globe size={14} className="text-orange-500" /> Active Protocols
                    </h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                        {Object.entries(protocolsObj).map(([protocolName, config]: [string, any]) => {
                            const isEnabled = config.enabled;
                            
                            return (
                                <div 
                                    key={protocolName} 
                                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                        isEnabled 
                                            ? "bg-emerald-50/60 dark:bg-emerald-500/5 border-emerald-200 dark:border-emerald-500/20 shadow-sm" 
                                            : "bg-slate-50 dark:bg-gray-900/40 border-slate-200 dark:border-gray-700/50"
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-1.5 rounded-md ${isEnabled ? "bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-slate-200 dark:bg-gray-800 text-slate-500 dark:text-gray-500"}`}>
                                            {protocolName.toLowerCase() === "ssh" ? <Key size={14} /> :
                                             protocolName.toLowerCase().includes("http") ? <Globe size={14} /> :
                                             <Share2 size={14} />}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`text-[11px] font-black uppercase tracking-wider ${isEnabled ? "text-slate-800 dark:text-gray-200" : "text-slate-400 dark:text-gray-500"}`}>
                                                {protocolName}
                                            </span>
                                            <span className="text-[9px] font-mono text-slate-500 dark:text-gray-400 mt-0.5">
                                                Port: {config.port || "-"}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        {isEnabled ? (
                                            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-500/20">
                                                <CheckCircle2 size={12} /> Enabled
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500 dark:text-gray-500 bg-slate-100 dark:bg-gray-800 px-2 py-0.5 rounded border border-slate-200 dark:border-gray-700">
                                                <XCircle size={12} /> Disabled
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        
                        {Object.keys(protocolsObj).length === 0 && (
                            <div className="col-span-full flex flex-col items-center justify-center p-8 opacity-60">
                                <AlertCircle size={24} className="text-slate-400 dark:text-gray-500 mb-2" />
                                <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-gray-400">No protocols found</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}